// src/utils/obsFormat.js
// Formato degli overlay OBS: orizzontale 16:9 (default) oppure verticale 9:16
// per le dirette Instagram / YouTube da smartphone (?format=vertical).
//
// Le grafiche verticali sono disegnate su un palco logico 540x960 e scalate
// sulla Browser Source (1080x1920 consigliato → scala 2, testo nitido).
// In alto e in basso restano libere le zone coperte dall'interfaccia delle app
// (intestazione del profilo in alto, commenti e pulsanti in basso).

export const V_W = 540;
export const V_H = 960;

export function isVerticalFormat(params) {
  const f = String(params?.get?.('format') || '').toLowerCase();
  return f === 'vertical' || f === 'verticale' || f === '9x16' || f === '9:16' || f === 'v';
}

// Fascia di lavoro sul palco 540x960: in verticale le grafiche stanno SOLO nella
// parte bassa dello schermo, sopra la zona dei commenti, per lasciare libera la
// diretta. `band` è l'altezza della fascia, `bottom` quella riservata ai commenti.
const COMMENTS_H = 240;   // commenti e pulsanti di Instagram / YouTube
const MARGIN = 24;        // margine minimo con ?safe=0
const HEADER_H = 120;     // intestazione del profilo (solo se la grafica va in alto)
// Altezza predefinita della fascia per tipo di grafica.
export const BAND = { default: 260, map: 380 };

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

// Altezza della fascia: ?band=NNN (px del palco) oppure il valore predefinito.
export function bandHeight(params, kind = 'default') {
  const asked = Number(params?.get?.('band'));
  const base = Number.isFinite(asked) && asked > 0 ? asked : (BAND[kind] || BAND.default);
  return clamp(base, 120, V_H - MARGIN * 2);
}

// Spazi da lasciare sopra e sotto la grafica, secondo posizione e fascia.
// pos: 'bottom' (predefinito) | 'top' | 'middle'.
export function stageBox(params, pos = 'bottom', kind = 'default') {
  const band = bandHeight(params, kind);
  const noSafe = params?.get?.('safe') === '0';
  if (pos === 'top') {
    const top = noSafe ? MARGIN : HEADER_H;
    return { top, bottom: Math.max(MARGIN, V_H - top - band), band };
  }
  if (pos === 'middle') {
    const m = Math.max(MARGIN, (V_H - band) / 2);
    return { top: m, bottom: m, band };
  }
  const bottom = noSafe ? MARGIN : COMMENTS_H;
  return { top: Math.max(MARGIN, V_H - bottom - band), bottom, band };
}

// Posizione verticale da ?pos (bl/br/tl/tr/bc/tc, più mc = centro schermo).
// Il predefinito è in basso: le grafiche non coprono la diretta.
export function verticalPos(pos, fallback = 'bottom') {
  const p = String(pos || '').toLowerCase();
  if (p === 'mc' || p === 'c' || p === 'center') return 'middle';
  if (p.startsWith('t')) return 'top';
  if (p.startsWith('b')) return 'bottom';
  return fallback;
}

// Aggiunge ?format=vertical a un URL di overlay (mantiene gli altri parametri).
export function withFormat(url, vertical) {
  if (!vertical) return url;
  return `${url}${url.includes('?') ? '&' : '?'}format=vertical`;
}

// Scelta 16:9 / 9:16 per ogni overlay (chiave = percorso dopo /obs/, es.
// "graphics/gaps", "sponsors/banner", "sponsor" per i singoli sponsor).
// Condivisa fra pannello gara e pagina Sponsor, salvata nel browser della regia.
const STORAGE_KEY = 'rs.obsVertical';
export function loadObsVertical() {
  try {
    const v = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return v && typeof v === 'object' ? v : {};
  } catch {
    return {};
  }
}
export function saveObsVertical(map) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch { /* storage non disponibile */ }
}
